import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { searchPatients } from "../../../api/apiService";
import { sanitizeSearchQuery } from "../../../utils/inputValidation";
import Search from "../../../assets/images/comman/search-outline.svg";
import MenuHorizental from "../../../assets/images/comman/dots-horizontal-outline.svg";
import EditPatient from "../../../assets/images/OPD-patients/EditPatient.svg";
import Assessment from "../../../assets/images/OPD-patients/Assessment.svg";
import OPDIDPrint from "../../../assets/images/OPD-patients/print.svg";
import ManualPrescription from "../../../assets/images/OPD-patients/ManualPrescription .svg";
import PaymentReceipt from "../../../assets/images/OPD-patients/PaymentReceipt .svg";
import XRayReport from "../../../assets/images/OPD-patients/X-RayReport.svg";
import ScanningReport from "../../../assets/images/OPD-patients/ScanningReport.svg";
import LabReport from "../../../assets/images/OPD-patients/LabReport.svg";
import CancelConsultation from "../../../assets/images/OPD-patients/CancelConsultation .svg";
import NewVisitIcon from "../../../assets/images/OPD-patients/newVisit.svg";
import LinkAbhaButton from "./LinkAbhaButton";
import NotifyAbhaButton from "./NotifyAbhaButton";
import { getAgoTime } from "./utils";
import {
  ensurePatientHasVisit,
  patientHasNoVisits,
  calculateAge,
  formatPatientName,
  formatDobDisplay,
} from "../../../utils/patientVisitUtils";
import CreateVisitModal from "../../../components/common/CreateVisitModal";
import EditPatientModal from "../../../components/Patients/EditPatientModal";
import { useAuth } from "../../../context/AuthContext";
import { PERMISSIONS } from "../../../constants/permissions";
import ToastContainer, {
  useToast,
} from "../../../components/common/ToastContainer";

const PatientsLists = () => {
  const navigate = useNavigate();
  const { can } = useAuth();
  const { toasts, removeToast, showError } = useToast();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalPatients, setTotalPatients] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [navigatingPatientId, setNavigatingPatientId] = useState(null);
  const [showVisitModal, setShowVisitModal] = useState(false);
  const [patientForVisit, setPatientForVisit] = useState(null);
  const [activeTab, setActiveTab] = useState("All");
  const [showEditModal, setShowEditModal] = useState(false);
  const [patientToEdit, setPatientToEdit] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0); // To trigger list refresh

  // Fetch patients data from API
  useEffect(() => {
    const fetchPatients = async () => {
      try {
        setLoading(true);
        const response = await searchPatients(searchTerm, currentPage);

        if (response.success) {
          // Transform API data to match component structure
          const transformedPatients = response.data.patients.map((patient) => {
            const rawName =
              patient.name && typeof patient.name === "string" && patient.name.trim() && patient.name.trim() !== "undefined undefined"
                ? patient.name.trim()
                : patient.fullName && typeof patient.fullName === "string" && patient.fullName.trim() && patient.fullName.trim() !== "undefined undefined"
                ? patient.fullName.trim()
                : formatPatientName(patient) || "Patient";
            const fullName = rawName;

            const dobFormatted = formatDobDisplay(patient.dob);

            let computedAge = "";
            if (dobFormatted) {
              const calc = calculateAge(dobFormatted);
              if (calc && typeof calc === "number" && !isNaN(calc) && calc > 0 && calc < 130) {
                computedAge = calc;
              }
            }
            if (!computedAge && patient.age !== undefined && patient.age !== null && patient.age !== "") {
              const parsed = parseInt(String(patient.age).replace(/[^0-9]/g, ""), 10);
              if (!isNaN(parsed) && parsed > 0 && parsed < 130) {
                computedAge = parsed;
              }
            }

            const visitTimestamp =
              patient.lastVisitDate ||
              patient.time ||
              patient.appointmentDate ||
              patient.updatedAt ||
              patient.lastVisit ||
              patient.createdAt;

            const lastVisit = formatDate(visitTimestamp);
            const lastVisitAgo = visitTimestamp ? getTimeAgo(visitTimestamp) : "";

            // Helper function to safely get value or return empty string
            const safeGet = (value) => value || "";

            const isAbhaLinked = Boolean(
              patient.isAbhaLinked === true ||
              patient.isabhalinked === true ||
              patient.isABHALinked === true ||
              String(patient.isAbhaLinked).toLowerCase() === "true" ||
              String(patient.isabhalinked).toLowerCase() === "true" ||
              String(patient.isABHALinked).toLowerCase() === "true" ||
              (patient.maskedAbha && String(patient.maskedAbha).trim() && patient.maskedAbha !== "N/A") ||
              (patient.ABHANumber && String(patient.ABHANumber).trim() && patient.ABHANumber !== "N/A") ||
              (patient.abhaNumber && String(patient.abhaNumber).trim() && patient.abhaNumber !== "N/A") ||
              (patient.abhaaddress && String(patient.abhaaddress).trim()) ||
              (patient.abhaAddress && String(patient.abhaAddress).trim()) ||
              (patient.ABHAAddress && String(patient.ABHAAddress).trim())
            );

            const maskedAbha =
              patient.maskedAbha ||
              (patient.ABHANumber ? `**** ${String(patient.ABHANumber).slice(-4)}` : "") ||
              patient.formattedAbhaId ||
              (patient.abhaaddress || patient.abhaAddress || patient.ABHAAddress || "");

            return {
              id: patient._id || patient.id,
              _id: patient._id || patient.id,
              name: fullName,
              fullName: fullName,
              age: computedAge ? `${computedAge}yr` : "",
              calculatedAge: computedAge,
              dob: dobFormatted,
              isAbhaLinked,
              isabhalinked: isAbhaLinked,
              maskedAbha,
              abhaId: maskedAbha || (isAbhaLinked ? "Linked" : "N/A"),
              formattedAbhaId: maskedAbha || patient.ABHANumber || "",
              lastVisit,
              lastVisitAgo,
              opdId:
                safeGet(patient.opdId) || (patient._id || patient.id || "").slice(-8).toUpperCase(),
              queue:
                patient.queue !== undefined && patient.queue !== null
                  ? String(patient.queue).padStart(2, "0")
                  : "—",
              uhid:
                safeGet(patient.uhid || patient.UHID) ||
                (patient._id || patient.id || "").slice(-8).toUpperCase(),
              fee:
                safeGet(
                  patient.fee !== undefined && patient.fee !== ""
                    ? (typeof patient.fee === "number" ? `₹${patient.fee}` : patient.fee)
                    : patient.consultationFee
                    ? `₹${patient.consultationFee}`
                    : "",
                ) || "",
              time: safeGet(patient.time || patient.appointmentTime || "") || "",
              visitType: patient.visitType || "General OPD", // Added visitType
              date: formatDate(patient.lastVisitDate || patient.appointmentDate || patient.time || new Date()),
              doctor:
                safeGet(
                  patient.doctor ||
                  patient.consultingDoctor ||
                  patient.assignedDoctor ||
                  patient.lastVisitedDoctor ||
                  patient?.visits?.[0]?.doctor,
                ) || "",
              mobile: safeGet(patient.mobile),
              address: safeGet(patient.address),
              pincode: safeGet(patient.pincode),
              status: safeGet(patient.status),
              // Store all raw patient data for OPD Details page
              rawData: {
                ...patient,
                name: fullName,
                fullName: fullName,
                calculatedAge: computedAge,
                age: computedAge ? `${computedAge}yr` : "",
                isAbhaLinked,
                isabhalinked: isAbhaLinked,
                maskedAbha,
                formattedAbhaId: maskedAbha || patient.ABHANumber || "",
                // ABHA related fields
                ABHANumber: safeGet(patient.ABHANumber),
                ABHAAddress: safeGet(
                  patient.ABHAAddress ||
                    patient.abhaAddress ||
                    patient.abhaaddress,
                ),
                yearOfBirth:
                  patient.yearOfBirth ||
                  (patient.dob ? new Date(patient.dob).getFullYear() : null),
                bloodGroup: safeGet(patient.bloodGroup),
                allergies: patient.allergies || [],
                emergencyContact: safeGet(patient.emergencyContact),
                gender: safeGet(patient.gender),
                dateOfBirth: safeGet(patient.dob),
                email: safeGet(patient.email),
                // Medical information
                chronicConditions: patient.chronicConditions || [],
                currentMedications: patient.currentMedications || [],
                medicalHistory: patient.medicalHistory || [],
                // Visit information
                visitId: safeGet(patient.visitId),
                department: safeGet(patient.department),
                reasonForVisit: safeGet(patient.reasonForVisit),
                // Vital signs information
                height: safeGet(patient.height),
                weight: safeGet(patient.weight),
                bmi: safeGet(patient.bmi),
                bmiCategory: safeGet(patient.bmiCategory),
                bloodPressure: safeGet(patient.bloodPressure),
                bloodPressureStatus: safeGet(patient.bloodPressureStatus),
                pulseRate: safeGet(patient.pulseRate),
                pulseRateStatus: safeGet(patient.pulseRateStatus),
                temperature: safeGet(patient.temperature),
                temperatureStatus: safeGet(patient.temperatureStatus),
                spO2: safeGet(patient.spO2),
                spO2Status: safeGet(patient.spO2Status),
                vitalsTimestamp: safeGet(patient.vitalsTimestamp),
              },
            };
          });

          setPatients(transformedPatients);
          setTotalPatients(response.data.total || 0);
          setTotalPages(response.data.pages || 1);
        } else {
          setError("Failed to fetch patients data");
        }
      } catch (err) {
        console.error("Error fetching patients:", err);
        setError(err.message || "Failed to fetch patients data");
      } finally {
        setLoading(false);
      }
    };

    const timeoutId = setTimeout(() => {
      fetchPatients();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, currentPage, refreshTrigger]);

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    const options = { day: "2-digit", month: "short", year: "numeric" };
    return date.toLocaleDateString("en-GB", options);
  };

  // Helper function to get time ago
  const getTimeAgo = (dateString) => getAgoTime(dateString);

  // Function to handle patient click and navigate to OPD Details (creates visit if none)
  const handlePatientClick = async (patient) => {
    if (navigatingPatientId) return;

    if (patientHasNoVisits(patient)) {
      setPatientForVisit(patient);
      setShowVisitModal(true);
      return;
    }

    try {
      setNavigatingPatientId(patient.id ?? patient._id);
      const patientToUse = await ensurePatientHasVisit(patient);
      navigate(`/patient-detail/${patientToUse.id}`, {
        state: {
          patientData: patientToUse.rawData,
          patientId: patientToUse.id,
        },
      });
    } catch (err) {
      console.error("Failed to load patient details", err);
      showError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to load patient details. Please try again.",
      );
    } finally {
      setNavigatingPatientId(null);
    }
  };

  const handleVisitCreated = (result) => {
    const apiPatient = result.patientData ?? result;
    const existingRaw = patientForVisit?.rawData ?? patientForVisit;

    const mergedRawData = {
      ...existingRaw,
      ...apiPatient,
      visits: apiPatient.visits ?? existingRaw?.visits,
      visitId:
        apiPatient.visitId ??
        apiPatient.visits?.[0]?.visitId ??
        existingRaw?.visitId,
    };

    const effectivePatientId = mergedRawData._id || mergedRawData.id;
    navigate(`/OPDDetails?patientId=${encodeURIComponent(effectivePatientId)}`, {
      state: {
        patientData: mergedRawData,
        patientId: effectivePatientId,
      },
    });
  };

  const handleEditPatient = (patient) => {
    setPatientToEdit(patient.rawData || patient);
    setShowEditModal(true);
  };

  const handleUpdateSuccess = () => {
    setRefreshTrigger((prev) => prev + 1);
  };

  return (
    <>
      <style>
        {`
                    .patient-row-hover:hover {
                        background-color: #f8f9fa !important;
                        transition: background-color 0.2s ease;
                    }
                    .patient-row-hover:hover td {
                        background-color: inherit !important;
                    }
                `}
      </style>
      <div className="container-fluid">
        <div className="opd-page">
          <div className="header-section-new">
            <div className="header-left">
              <ul className="nav nav-tabs-new">
                <li className="nav-item">
                  <span
                    className={`nav-link ${activeTab === "All" ? "active" : ""}`}
                    onClick={() => setActiveTab("All")}
                  >
                    All
                  </span>
                </li>
                <li className="nav-item">
                  <span
                    className={`nav-link ${activeTab === "OPD" ? "active" : ""}`}
                    onClick={() => setActiveTab("OPD")}
                  >
                    OPD
                  </span>
                </li>
                <li className="nav-item">
                  <span
                    className={`nav-link ${activeTab === "IPD" ? "active" : ""}`}
                    onClick={() => setActiveTab("IPD")}
                  >
                    IPD
                  </span>
                </li>
                <li className="nav-item">
                  <span
                    className={`nav-link ${activeTab === "Emergency" ? "active" : ""}`}
                    onClick={() => setActiveTab("Emergency")}
                  >
                    Emergency Cases
                  </span>
                </li>
              </ul>
            </div>
            <div className="header-right">
              <div className="search-bar-new">
                <span className="search-icon">
                  <img src={Search} alt="Search" />
                </span>
                <input
                  type="text"
                  placeholder="Search by Name, Mob, ABHA"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(sanitizeSearchQuery(e.target.value));
                    setCurrentPage(1);
                  }}
                />
              </div>
              <button
                className="btn-refresh-new"
                onClick={() => window.location.reload()}
                disabled={loading}
              >
                {loading ? "Refreshing..." : "↻ Refresh"}
              </button>
              {can(PERMISSIONS.PATIENTS_REGISTER) && (
                <Link to="/add-patient" className="btn-add-new">
                  + Add Patient
                </Link>
              )}
            </div>
          </div>

          <div className="OpdBodySection">
            <div className="tab-content">
              <div id="All" className=" tab-pane active">
                <br />
                <div className="opdAllTable">
                  <table className="table table-bordered align-middle">
                    <thead>
                      <tr>
                        <th>Patient Name</th>
                        <th>OPD ID</th>
                        <th>Queue</th>
                        <th>ABHA ID</th>
                        <th>UHID</th>
                        <th>Fee</th>
                        <th>Time</th>
                        <th>Doctor</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="9" className="text-center py-4">
                            <div
                              className="spinner-border text-primary"
                              role="status"
                            >
                              <span className="visually-hidden">
                                Loading...
                              </span>
                            </div>
                            <p className="mt-2 mb-0">Loading patients...</p>
                          </td>
                        </tr>
                      ) : error ? (
                        <tr>
                          <td colSpan="9" className="text-center py-4">
                            <div className="alert alert-danger mb-0">
                              <strong>Error:</strong> {error}
                              <br />
                              <button
                                className="btn btn-sm btn-outline-primary mt-2"
                                onClick={() => window.location.reload()}
                              >
                                Retry
                              </button>
                            </div>
                          </td>
                        </tr>
                      ) : patients.length === 0 ? (
                        <tr>
                          <td colSpan="9" className="text-center py-4">
                            <div className="alert alert-info mb-0">
                              <strong>No patients found.</strong>
                              <br />
                              {can(PERMISSIONS.PATIENTS_REGISTER) && (
                                <Link
                                  to="/add-patient"
                                  className="btn btn-sm btn-primary mt-2"
                                >
                                  Add First Patient
                                </Link>
                              )}
                            </div>
                          </td>
                        </tr>
                      ) : (
                        patients
                          .filter((patient) => {
                            if (
                              activeTab === "OPD" &&
                              patient.visitType !== "General OPD"
                            )
                              return false;
                            if (
                              activeTab === "IPD" &&
                              patient.visitType !== "IPD"
                            )
                              return false;
                            if (
                              activeTab === "Emergency" &&
                              patient.visitType !== "Emergency"
                            )
                              return false;
                            return true;
                          })
                          .map((patient, index) => (
                            <tr
                              key={patient.id || index}
                              style={{
                                cursor: navigatingPatientId
                                  ? "wait"
                                  : "pointer",
                                opacity:
                                  navigatingPatientId ===
                                  (patient.id ?? patient._id)
                                    ? 0.7
                                    : 1,
                              }}
                              onClick={() => handlePatientClick(patient)}
                              title="Click to view patient details"
                              className="patient-row-hover"
                            >
                              <td>
                                <div className="patientDetails">
                                  <h5>{patient.name}</h5>
                                  <p>
                                    {patient.age ? `Age: ${patient.age}` : ""}
                                    {patient.dob ? `${patient.age ? " | " : ""}DOB: ${patient.dob}` : ""}
                                  </p>
                                  {patient.lastVisit ? (
                                    <p>
                                      Last Visit: {patient.lastVisit}
                                      {patient.lastVisitAgo ? ` (${patient.lastVisitAgo})` : ""}
                                    </p>
                                  ) : null}
                                  {patient.mobile && (
                                    <p className="text-muted small">
                                      Mobile: {patient.mobile}
                                    </p>
                                  )}
                                  {navigatingPatientId ===
                                    (patient.id ?? patient._id) && (
                                    <p className="text-primary small mb-0 mt-1">
                                      <span
                                        className="spinner-border spinner-border-sm me-1"
                                        role="status"
                                        aria-hidden="true"
                                      />
                                      Creating visit…
                                    </p>
                                  )}
                                </div>
                              </td>
                              <td>
                                <h5>{patient.opdId}</h5>
                              </td>
                              <td>
                                <h5>
                                  {patient.queue.toString().padStart(2, "0")}
                                </h5>
                              </td>
                              <td onClick={(e) => e.stopPropagation()}>
                                {patient.isAbhaLinked || (patient.abhaId && patient.abhaId !== "N/A") ? (
                                  <div>
                                    <h5 style={{ fontFamily: "monospace", margin: 0 }}>
                                      {patient.maskedAbha || patient.abhaId || "Linked"}
                                    </h5>
                                    {patient.ABHAAddress && (
                                      <small className="text-muted">
                                        {patient.ABHAAddress}
                                      </small>
                                    )}
                                  </div>
                                ) : (
                                  <div className="d-flex flex-column gap-1">
                                    <LinkAbhaButton patient={patient} />
                                    <NotifyAbhaButton patientId={patient.id} />
                                  </div>
                                )}
                              </td>
                              <td>
                                <h5>{patient.uhid}</h5>
                              </td>
                              <td>
                                <h5>{patient.fee || ""}</h5>
                              </td>
                              <td>
                                <div className="patientTime">
                                  <h5>{patient.time || ""}</h5>
                                  <p>{patient.date}</p>
                                </div>
                              </td>
                              <td>
                                <h5>{patient.doctor || ""}</h5>
                              </td>
                              <td>
                                <div
                                  className="dropdown"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <div
                                    className="menudropdown"
                                    id={`dropdownMenuButton${index}`}
                                    data-bs-toggle="dropdown"
                                    aria-expanded="false"
                                  >
                                    <img src={MenuHorizental} alt="menu" />
                                  </div>
                                  <ul
                                    className="dropdown-menu dropdown-menu-end"
                                    aria-labelledby={`dropdownMenuButton${index}`}
                                  >
                                    <li>
                                      <button
                                        className="dropdown-item"
                                        type="button"
                                        onClick={() => {
                                          setPatientForVisit(patient);
                                          setShowVisitModal(true);
                                        }}
                                      >
                                        <img
                                          src={NewVisitIcon}
                                          alt="New Visit"
                                          style={{
                                            width: "16px",
                                            height: "16px",
                                            marginRight: "8px",
                                          }}
                                        />{" "}
                                        New Visit
                                      </button>
                                    </li>
                                    <li>
                                      <button
                                        className="dropdown-item"
                                        type="button"
                                        onClick={() =>
                                          handleEditPatient(patient)
                                        }
                                      >
                                        <img src={EditPatient} alt="Edit" />{" "}
                                        Edit Patient
                                      </button>
                                    </li>
                                    <li>
                                      <Link
                                        className="dropdown-item"
                                        to={`/opd-assessment?patientId=${patient.id}`}
                                        state={{
                                          patientData: patient.rawData,
                                          patientId: patient.id,
                                        }}
                                      >
                                        <img
                                          src={Assessment}
                                          alt="Assessment"
                                        />{" "}
                                        Assessment{" "}
                                      </Link>
                                    </li>
                                    {/* <li>
                                      <button
                                        className="dropdown-item"
                                        type="button"
                                      >
                                        <img src={OPDIDPrint} alt="Print" /> OPD
                                        ID Print
                                      </button>
                                    </li> */}
                                    <li>
                                      <Link
                                        className="dropdown-item"
                                        to={`/OPDDetails?patientId=${patient.id}`}
                                        state={{
                                          patientData: patient.rawData,
                                          patientId: patient.id,
                                        }}
                                      >
                                        <img
                                          src={ManualPrescription}
                                          alt="Prescription"
                                        />{" "}
                                        Manual Prescription{" "}
                                      </Link>
                                    </li>
                                    {/* <li>
                                      <button
                                        className="dropdown-item"
                                        type="button"
                                      >
                                        <img
                                          src={PaymentReceipt}
                                          alt="Receipt"
                                        />{" "}
                                        Payment Receipt{" "}
                                      </button>
                                    </li> */}
                                    {/* <li>
                                      <button
                                        className="dropdown-item"
                                        type="button"
                                      >
                                        <img src={XRayReport} alt="X-Ray" />{" "}
                                        X-Ray Report
                                      </button>
                                    </li> */}
                                    {/* <li>
                                      <button
                                        className="dropdown-item"
                                        type="button"
                                      >
                                        <img src={ScanningReport} alt="Scan" />{" "}
                                        Scanning Report
                                      </button>
                                    </li> */}
                                    <li>
                                      <Link
                                        className="dropdown-item"
                                        to={`/OPDDetails?patientId=${patient.id}`}
                                        state={{
                                          patientData: patient.rawData,
                                          patientId: patient.id,
                                        }}
                                      >
                                        <img src={LabReport} alt="Lab" /> Lab
                                        Report
                                      </Link>
                                    </li>
                                    {/* <li>
                                      <button
                                        className="dropdown-item"
                                        type="button"
                                      >
                                        <img
                                          src={CancelConsultation}
                                          alt="Cancel"
                                        />{" "}
                                        Cancel Consultation{" "}
                                      </button>
                                    </li> */}
                                  </ul>
                                </div>
                              </td>
                            </tr>
                          ))
                      )}
                    </tbody>
                  </table>
                  {/* Show total patients count */}
                  {/* Pagination Controls */}
                  {!loading && !error && totalPages > 1 && (
                    <div className="d-flex justify-content-between align-items-center mt-3">
                      <div className="text-muted">
                        <small>
                          Total Patients: {totalPatients} | Page {currentPage}{" "}
                          of {totalPages}
                        </small>
                      </div>
                      <nav aria-label="Patient list pagination">
                        <ul className="pagination pagination-sm mb-0">
                          <li
                            className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                          >
                            <button
                              className="page-link"
                              onClick={() =>
                                setCurrentPage((prev) => Math.max(prev - 1, 1))
                              }
                              disabled={currentPage === 1}
                            >
                              Previous
                            </button>
                          </li>
                          {[...Array(totalPages)].map((_, i) => (
                            <li
                              key={i + 1}
                              className={`page-item ${currentPage === i + 1 ? "active" : ""}`}
                            >
                              <button
                                className="page-link"
                                onClick={() => setCurrentPage(i + 1)}
                              >
                                {i + 1}
                              </button>
                            </li>
                          ))}
                          <li
                            className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
                          >
                            <button
                              className="page-link"
                              onClick={() =>
                                setCurrentPage((prev) =>
                                  Math.min(prev + 1, totalPages),
                                )
                              }
                              disabled={currentPage === totalPages}
                            >
                              Next
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  )}

                  {!loading &&
                    !error &&
                    patients.length > 0 &&
                    totalPages <= 1 && (
                      <div className="mt-3 text-muted">
                        <small>Total Patients: {totalPatients}</small>
                      </div>
                    )}
                </div>
              </div>
              <div id="EmergencyCases" className=" tab-pane fade">
                <br />
                <h3>EmergencyCases</h3>
                <p>
                  Ut enim ad minim veniam, quis nostrud exercitation ullamco
                  laboris nisi ut aliquip ex ea commodo consequat.
                </p>
              </div>
              <div id="Consultation" className=" tab-pane fade">
                <br />
                <h3>Consultation</h3>
                <p>
                  Sed ut perspiciatis unde omnis iste natus error sit voluptatem
                  accusantium doloremque laudantium, totam rem aperiam.
                </p>
              </div>
              <div id="MedicalRecords" className=" tab-pane fade">
                <br />
                <h3>MedicalRecords</h3>
                <p>
                  Sed ut perspiciatis unde omnis iste natus error sit voluptatem
                  accusantium doloremque laudantium, totam rem aperiam.
                </p>
              </div>
              <div id="DischargeSummary" className=" tab-pane fade">
                <br />
                <h3>DischargeSummary</h3>
                <p>
                  Sed ut perspiciatis unde omnis iste natus error sit voluptatem
                  accusantium doloremque laudantium, totam rem aperiam.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <CreateVisitModal
        isOpen={showVisitModal}
        onClose={() => setShowVisitModal(false)}
        patientId={patientForVisit?.id}
        patientName={patientForVisit?.name}
        patientMeta={
          patientForVisit
            ? `${patientForVisit.age} / ${patientForVisit.mobile || ""} / ${patientForVisit.address || ""}`
                .replace(/\s*\/\s*\/\s*/g, " / ")
                .trim()
                .replace(/\/$/, "")
                .trim()
            : ""
        }
        onVisitCreated={handleVisitCreated}
      />
      <EditPatientModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        patientData={patientToEdit}
        onUpdateSuccess={handleUpdateSuccess}
      />
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default PatientsLists;
